// src/utils/questionEngineOtpHelpers.js
import { getFlag, subscribeFlags } from '../experiments/flags';

// Feature flag `otp_sms`: the default lives in src/experiments/flags.config.js
// and the Firebase console can override it without a deploy. Email OTP is
// unaffected — every flow that would otherwise default to (or offer) SMS
// falls back to email while this is `false`. Kept as a live binding so the
// many call sites keep reading a plain boolean; it updates whenever the
// remote document is applied, before the next route renders.
export let ENABLE_OTP_SMS = getFlag('otp_sms');
subscribeFlags(() => {
  ENABLE_OTP_SMS = getFlag('otp_sms');
});

// ---------- generic JSON / localStorage helper ----------
export const safeParse = (raw, fb = null) => {
  try {
    return raw ? JSON.parse(raw) : fb;
  } catch {
    return fb;
  }
};

// ---------- sessionStorage keys per product ----------
export const otpSessionKeyForProduct = (product) =>
  product ? `otpVerified:${product}` : 'otpVerified:__unknown__';

export const isOtpAlreadyVerifiedForProduct = (product) => {
  if (typeof window === 'undefined') return false;
  const key = otpSessionKeyForProduct(product);
  const v = sessionStorage.getItem(key);
  return v === 'true';
};

export const markOtpVerifiedForProduct = (product) => {
  try {
    const key = otpSessionKeyForProduct(product);
    sessionStorage.setItem(key, 'true');
  } catch {
    // ignore
  }
};

export const markOtpBypassedForProduct = (product) => {
  try {
    const key = otpSessionKeyForProduct(product);
    sessionStorage.setItem(key, 'bypassed');
  } catch {
    // ignore
  }
};

// ---------- OTP step-up "shown once per session" (question flow only) ----------
// Mirrors the marketing modal's session-based suppression: once the customer
// has seen the OTP verification modal ("Let's keep your information safe")
// during the question flow, we don't re-prompt — or re-send an OTP — for the
// rest of the browser session. Cleared when sessionStorage is (e.g. sign out).
// The homepage returning-user and sign-in flows do not read this flag, so they
// keep their existing behaviour.
const OTP_STEPUP_SHOWN_KEY = 'otpStepupShown';

export const markOtpStepupShown = () => {
  try {
    sessionStorage.setItem(OTP_STEPUP_SHOWN_KEY, '1');
  } catch {
    // ignore
  }
};

export const wasOtpStepupShown = () => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(OTP_STEPUP_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
};

// ---------- cell number + email + OTP modal helpers ----------
const maskCell = (cell) => {
  if (!cell) return '';
  const clean = String(cell).replace(/\D/g, '');
  return clean.replace(
    /(\d)(\d)(\d)(\d{3})(\d{2})(\d{2})/,
    (_, a, b, c, d, e, f) => `${a}•${c} ••• ••${f}`
  );
};

const maskEmail = (email) => {
  if (!email) return '';
  const [local, domain] = String(email).split('@');
  if (!domain) return email;
  const visible =
    local.length <= 2
      ? local
      : local[0] +
        '•'.repeat(Math.min(local.length - 2, 4)) +
        local[local.length - 1];
  return `${visible}@${domain}`;
};

export const normalizeCellNumber = (cell) => {
  if (!cell) return '';
  let s = String(cell).trim().replace(/\s+/g, '');
  if (s.startsWith('+27')) s = '0' + s.slice(3);
  if (s.startsWith('27')) s = '0' + s.slice(2);
  return s;
};

// A valid South African contact number: exactly 10 digits, starting with 0.
// Covers both cellphone and landline numbers (both use this format).
export const SA_CONTACT_NUMBER_REGEX = /^0\d{9}$/;

export const isValidSaContactNumber = (value) =>
  SA_CONTACT_NUMBER_REGEX.test(normalizeCellNumber(value).replace(/\D/g, ''));

const otpInputQuestion = {
  layout: 'column',
  otp_code: {
    type: 'otp-input',
    orderNumber: 0,
    length: 4,
    validation: {
      required: true,
      message: 'Please enter the 4-digit code',
    },
  },
};

// Shown before any OTP is sent — lets the user opt in (or bypass) instead of
// an SMS firing automatically as soon as their cell number is captured.
export const buildConfirmSendOtpModal = (cell, options = {}) => {
  const { returningUser = false } =
    typeof options === 'object' && options !== null ? options : {};
  return {
    title: "Let's keep your information safe",
    subtitle:
      'Verify your number to securely access your details and make future quotes quicker & easier.',
    content: `We'll send a verification code to:\n${maskCell(cell)}`,
    type: 'otp-confirm-send',
    buttons: returningUser
      ? [
          { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
          {
            index: 1,
            id: 'no-thats-not-me',
            type: 'third-gray',
            text: "That's not me",
          },
        ]
      : [
          { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
          {
            index: 1,
            id: 'guest-quote',
            type: 'third-gray',
            text: 'Continue without verifying',
            bold: true,
          },
          {
            index: 2,
            id: 'use-email-instead',
            type: 'third-gray',
            text: 'Send code by email instead',
          },
        ],
  };
};

export const buildEnterOtpModal = (cell, options = {}) => {
  const { returningUser = false } =
    typeof options === 'object' && options !== null ? options : {};
  return {
    title: "Let's keep your information safe",
    subtitle:
      'Verification helps protect and secure your personal information.',
    content: `We've sent a verification code to\n${maskCell(cell)}`,
    type: 'otp-input',
    questions: [otpInputQuestion],
    buttons: returningUser
      ? [
          {
            index: 1,
            id: 'no-thats-not-me',
            type: 'third-gray',
            text: "That's not me",
          },
        ]
      : [
          {
            index: 1,
            id: 'guest-quote',
            type: 'third-gray',
            text: 'Continue without verifying',
            bold: true,
          },
          {
            index: 2,
            id: 'use-email-instead',
            type: 'third-gray',
            text: 'Send code by email instead',
          },
        ],
  };
};

export const buildEnterEmailOtpModal = (email, options = {}) => {
  const { returningUser = false } =
    typeof options === 'object' && options !== null ? options : {};
  return {
    title: "Let's keep your information safe",
    subtitle:
      'Verification helps protect and secure your personal information.',
    content: `We've sent a verification code to\n${maskEmail(email)}`,
    type: 'otp-input',
    questions: [otpInputQuestion],
    buttons: returningUser
      ? [
          {
            index: 1,
            id: 'no-thats-not-me',
            type: 'third-gray',
            text: "That's not me",
          },
        ]
      : [
          {
            index: 1,
            id: 'guest-quote',
            type: 'third-gray',
            text: 'Continue without verifying',
            bold: true,
          },
        ],
  };
};

export const buildRequestOtpModal = (
  firstNameHint,
  { via = 'phone', prefill = '', returningUser = false } = {}
) => ({
  title: firstNameHint
    ? `Is that you, ${firstNameHint}?`
    : "Let's get you signed in",
  content: "Let's send you an OTP so we can get your quotes faster",
  type: 'otp-request',
  questions: [
    {
      layout: 'column',
      ...(via === 'email'
        ? {
            email_address: {
              type: 'text-field',
              orderNumber: 0,
              value: prefill,
              placeholder: 'Enter your email address',
              validation: {
                regex: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
                message: 'Please enter a valid email address',
              },
            },
          }
        : {
            contact_number: {
              type: 'text-field',
              orderNumber: 0,
              value: prefill,
              onlyNumbers: true,
              placeholder: 'Enter your cell number',
              validation: {
                regex: '^0\\d{9}$',
                message: 'Please enter a valid South African cellphone number',
              },
            },
          }),
    },
  ],
  buttons: returningUser
    ? [
        { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
        {
          index: 1,
          id: 'no-thats-not-me',
          type: 'secondary-gray',
          text: "No, that's not me",
        },
      ]
    : [
        { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
        {
          index: 0,
          id: 'first-time-quoting',
          type: 'secondary-gray',
          text: "It's My First Time Quoting",
        },
      ],
});

// ---------- OTP validation window ----------
export const BYPASS_WINDOW_MS = 8 * 60 * 60 * 1000; // 8 hours (fallback for legacy sessions)

export const JWT_EXPIRED_EVENT = 'hippo:jwt-expired';

export const isValidatedWithin = () => {
  const session = safeParse(localStorage.getItem('authSession'), {});

  // Trust the server-issued JWT expiry date — no legacy fallback.
  const jwtToken = session?.jwtToken;
  const jwtValidTo = session?.jwtValidTo;
  if (!jwtToken || !jwtValidTo) return false;

  const expiry = new Date(jwtValidTo).getTime();
  return !isNaN(expiry) && Date.now() < expiry;
};

export const isBypassedWithin = (ms = BYPASS_WINDOW_MS) => {
  const session = safeParse(localStorage.getItem('authSession'), {});
  const bypassedAt = session?.bypassedAt;
  if (!bypassedAt) return false;
  return Date.now() - bypassedAt < ms;
};

// ---------- localStorage persistence ----------
export const AUTH_SESSION_UPDATED_EVENT = 'hippo:auth-session-updated';

const dispatchAuthSessionUpdated = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_UPDATED_EVENT));
};

export const persistAuthSession = (partial) => {
  const next = {
    ...(safeParse(localStorage.getItem('authSession'), {}) || {}),
    ...partial,
  };
  localStorage.setItem('authSession', JSON.stringify(next));
  dispatchAuthSessionUpdated();
  return next;
};

export const persistReturnUserDetails = (payload) => {
  localStorage.setItem('returnUserDetails', JSON.stringify(payload));
  dispatchAuthSessionUpdated();
};

export const clearPersistedAuthSession = () => {
  localStorage.removeItem('authSession');
  localStorage.removeItem('returnUserDetails');
  localStorage.removeItem('accountDataCache');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('hippo:account-data-updated'));
  }
  dispatchAuthSessionUpdated();
};

export const resolveStoredProfileCellNumber = () => {
  const authSession = safeParse(localStorage.getItem('authSession'), {});
  const formValues = safeParse(localStorage.getItem('formValues'), {});
  const returnUserDetails = safeParse(
    localStorage.getItem('returnUserDetails'),
    {}
  );

  return normalizeCellNumber(
    authSession?.cellNumber ||
      formValues?.cellNumber ||
      formValues?.phone ||
      formValues?.CellNumber ||
      returnUserDetails?.cellNumber ||
      returnUserDetails?.CellNumber ||
      ''
  );
};

export const buildPolicyHolderName = (person = {}) => {
  const explicitName =
    person?.PolicyHolderName ||
    person?.policyHolderName ||
    person?.FullName ||
    person?.fullName ||
    person?.Name ||
    '';

  const firstName =
    person?.FirstName || person?.firstName || person?.PolicyHolderFirstName;
  const lastName =
    person?.LastName || person?.lastName || person?.PolicyHolderLastName;

  return (
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    String(explicitName).trim()
  );
};

export const resolveStoredPolicyHolderName = () => {
  // Only show a name if the user has a currently valid OTP session.
  if (!isValidatedWithin()) return '';

  const authSession = safeParse(localStorage.getItem('authSession'), {});
  const formValues = safeParse(localStorage.getItem('formValues'), {});
  const returnUserDetails = safeParse(
    localStorage.getItem('returnUserDetails'),
    {}
  );

  return (
    authSession?.profileName ||
    buildPolicyHolderName(returnUserDetails) ||
    buildPolicyHolderName(formValues) ||
    ''
  );
};
